/**
 * NPCBehavior — state machine for NPC entities (Babylon.js port).
 *
 * NPCs cycle through states based on player proximity and ability usage.
 * The "calm meter" pattern mirrors CrystalReceiver charging.
 *
 * Engine-agnostic: operates on world-unit positions (Vector3.x / .y).
 * The owning NPC entity translates velocities into Babylon physics or
 * direct mesh movement.
 */

export type NPCState =
  | 'idle'
  | 'patrol'
  | 'frantic'
  | 'calming'
  | 'calmed'
  | 'fleeing'
  | 'following';

export interface NPCBehaviorConfig {
  initialState: NPCState;
  /** Radius at which Minna's Purr begins calming this NPC (world units). */
  calmRadius?: number;
  /** Charge needed to fully calm (0-100). */
  calmThreshold?: number;
  /** Charge per second when in purr range. */
  calmRate?: number;
  /** Charge decay per second when NOT in purr range. */
  calmDecay?: number;
  /** Patrol waypoints (x positions in world units). */
  patrolPoints?: number[];
  /** Patrol speed in world units/s. */
  patrolSpeed?: number;
  /** Follow speed in world units/s. */
  followSpeed?: number;
  /** Distance to maintain when following (world units). */
  followDistance?: number;
}

const DEFAULTS: Required<Omit<NPCBehaviorConfig, 'initialState'>> = {
  calmRadius: 6.0,
  calmThreshold: 100,
  calmRate: 30,
  calmDecay: 10,
  patrolPoints: [],
  patrolSpeed: 4.0,
  followSpeed: 7.0,
  followDistance: 3.0,
};

export class NPCBehavior {
  state: NPCState;
  readonly config: Required<Omit<NPCBehaviorConfig, 'initialState'>>;

  /** Current calm meter (0 to threshold). */
  calmMeter = 0;

  /** Current patrol waypoint index. */
  private patrolIndex = 0;
  private patrolDirection = 1;

  /** Follow target position (world units). */
  followTarget?: { x: number; y: number };

  /** Frantic jitter timer. */
  private jitterTimer = 0;
  private jitterDirection = 1;

  constructor(cfg: NPCBehaviorConfig) {
    this.state = cfg.initialState;
    this.config = { ...DEFAULTS, ...cfg };
  }

  /**
   * Advance the state machine by `deltaSeconds`.
   * Returns the desired horizontal velocity (world units/s) for the NPC.
   */
  update(
    deltaSeconds: number,
    npcX: number,
    npcY: number,
    purring: boolean,
    purrX: number,
    purrY: number,
  ): number {
    switch (this.state) {
      case 'frantic':
        return this.updateFrantic(deltaSeconds, npcX, npcY, purring, purrX, purrY);
      case 'calming':
        return this.updateCalming(deltaSeconds, npcX, npcY, purring, purrX, purrY);
      case 'calmed':
        return 0;
      case 'patrol':
        return this.updatePatrol(npcX);
      case 'following':
        return this.updateFollowing(npcX);
      case 'fleeing':
        return this.updateFleeing(npcX, purrX);
      case 'idle':
      default:
        return 0;
    }
  }

  // ── State updaters ──────────────────────────────────────────────

  private updateFrantic(
    dt: number,
    npcX: number,
    npcY: number,
    purring: boolean,
    purrX: number,
    purrY: number,
  ): number {
    if (purring) {
      const dx = npcX - purrX;
      const dy = npcY - purrY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= this.config.calmRadius) {
        this.state = 'calming';
        return 0;
      }
    }

    this.calmMeter = Math.max(0, this.calmMeter - this.config.calmDecay * dt);

    // Jittery movement
    this.jitterTimer += dt;
    if (this.jitterTimer > 0.3 + Math.random() * 0.4) {
      this.jitterTimer = 0;
      this.jitterDirection = Math.random() > 0.5 ? 1 : -1;
    }
    return this.jitterDirection * this.config.patrolSpeed * 0.6;
  }

  private updateCalming(
    dt: number,
    npcX: number,
    npcY: number,
    purring: boolean,
    purrX: number,
    purrY: number,
  ): number {
    if (purring) {
      const dx = npcX - purrX;
      const dy = npcY - purrY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= this.config.calmRadius) {
        this.calmMeter += this.config.calmRate * dt;
        if (this.calmMeter >= this.config.calmThreshold) {
          this.calmMeter = this.config.calmThreshold;
          this.state = 'calmed';
        }
        return 0; // Stand still while being calmed
      }
    }

    // Purr stopped or out of range — decay and revert to frantic
    this.calmMeter = Math.max(0, this.calmMeter - this.config.calmDecay * dt);
    if (this.calmMeter <= 0) {
      this.state = 'frantic';
    }
    return 0;
  }

  private updatePatrol(npcX: number): number {
    if (this.config.patrolPoints.length < 2) return 0;

    const target = this.config.patrolPoints[this.patrolIndex];
    const dist = target - npcX;

    if (Math.abs(dist) < 0.25) {
      this.patrolIndex += this.patrolDirection;
      if (this.patrolIndex >= this.config.patrolPoints.length || this.patrolIndex < 0) {
        this.patrolDirection *= -1;
        this.patrolIndex += this.patrolDirection * 2;
      }
      return 0;
    }

    return Math.sign(dist) * this.config.patrolSpeed;
  }

  private updateFollowing(npcX: number): number {
    if (!this.followTarget) return 0;

    const dist = this.followTarget.x - npcX;
    if (Math.abs(dist) < this.config.followDistance) return 0;

    return Math.sign(dist) * this.config.followSpeed;
  }

  private updateFleeing(npcX: number, threatX: number): number {
    const dir = npcX < threatX ? -1 : 1;
    return dir * this.config.patrolSpeed * 1.5;
  }

  // ── Public helpers ──────────────────────────────────────────────

  /** Transition to a new state. */
  setState(newState: NPCState): void {
    this.state = newState;
  }

  /** Get calm progress as 0-1 fraction. */
  get calmPercent(): number {
    return this.calmMeter / this.config.calmThreshold;
  }

  /** Check if fully calmed. */
  get isCalmed(): boolean {
    return this.state === 'calmed';
  }
}
